import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CombinedPlot } from '@/components/MeasurementAnalysis/Charts/CombinedPlot';
import { testService } from '@/services/api/testService';
import logger from '@/lib/logger';
import type { MeasurementResult } from '@/types/device';
import type { TestChartData, MeasurementData, ChannelData, ImpedancePoint } from '@/lib/impedance';

export interface BaselineReviewOverlayProps {
  open: boolean;
  testId: string | null;
  sampleCode: string;
  baselineResult: MeasurementResult;
  baselineCompletedAt: Date;
  onProceed: () => void;
  onDiscard: () => void;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Builds chart data for the baseline review overlay from every channel in the
 * stored result — `impedanceResult.data[]` already holds one entry per requested
 * channel, so this maps each entry to a `ChannelData` directly (no de-interleaving
 * needed) rather than picking a single channel.
 */
export function buildBaselineTestChartData(
  testId: string | null,
  baselineResult: MeasurementResult | null | undefined
): TestChartData {
  const frequencies = baselineResult?.impedanceResult?.frequencyHz ?? [];
  const data = baselineResult?.impedanceResult?.data ?? [];

  const channels: ChannelData[] = data.map((channelData, channelIndex) => {
    // A MEASURE_ABORT-interrupted channel can legitimately report fewer points
    // than frequencies (proto's own documented behavior). Truncate to the
    // shortest array rather than indexing past a short channel's end, which
    // would silently produce undefined magnitude/phase and NaN-poisoned points.
    const length = Math.min(frequencies.length, channelData.magnitudeOhm.length, channelData.phaseRad.length);
    if (length !== frequencies.length || length !== channelData.magnitudeOhm.length || length !== channelData.phaseRad.length) {
      logger.warn('[buildBaselineTestChartData] channel array length mismatch, truncating', {
        channelIndex,
        frequencies: frequencies.length,
        magnitudeOhm: channelData.magnitudeOhm.length,
        phaseRad: channelData.phaseRad.length,
      });
    }

    const points: ImpedancePoint[] = frequencies.slice(0, length).map((freq, i) => {
      const magnitude = channelData.magnitudeOhm[i];
      const phase = channelData.phaseRad[i];
      return {
        frequency: freq,
        magnitude,
        phase,
        phaseDeg: phase * (180 / Math.PI),
        zReal: magnitude * Math.cos(phase),
        zImag: magnitude * Math.sin(-phase),
      };
    });
    return {
      channelId: `CH${channelIndex + 1}`,
      channelIndex,
      points,
    };
  });

  const baseline: MeasurementData = {
    channels,
    frequencies,
    channelCount: channels.length,
  };

  return {
    testId: testId ?? 'preview',
    label: 'Baseline Review',
    baseline,
    specimen: null,
  };
}

export function BaselineReviewOverlay({
  open,
  testId,
  sampleCode,
  baselineResult,
  baselineCompletedAt,
  onProceed,
  onDiscard,
}: BaselineReviewOverlayProps) {
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);

  // Elapsed timer — calculates real elapsed time from baselineCompletedAt
  useEffect(() => {
    if (!open) return;

    const tick = () =>
      setElapsed(Math.floor((Date.now() - baselineCompletedAt.getTime()) / 1000));

    tick(); // immediate update on open
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [open, baselineCompletedAt]);

  // Escape → Proceed (never Discard)
  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmingDiscard) {
        onProceed();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, confirmingDiscard, onProceed]);

  // Reset confirmation state when overlay closes
  useEffect(() => {
    if (!open) {
      setConfirmingDiscard(false);
      setDiscardError(null);
    }
  }, [open]);

  const testChartData = useMemo(
    () => buildBaselineTestChartData(testId, baselineResult),
    [testId, baselineResult]
  );

  if (!open) return null;

  const handleDiscardConfirm = async () => {
    setDiscarding(true);
    setDiscardError(null);
    try {
      if (testId) {
        await testService.cancelTest(testId);
      }
      onDiscard();
      navigate('/');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to cancel measurement. Please try again.';
      setDiscardError(msg);
      setDiscarding(false);
    }
  };

  return (
    <Box
      data-testid="baseline-review-overlay"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        bgcolor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '2vh 2vw',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            minHeight: 52,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            Baseline Review — Sample: {sampleCode}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatElapsed(elapsed)} since baseline
          </Typography>
        </Box>

        {/* Chart — fills remaining space */}
        <Box sx={{ flex: 1, overflow: 'hidden', px: 2, py: 1, minHeight: 0 }}>
          <CombinedPlot
            testData={[testChartData]}
            visibleChannels={new Set(['CH1', 'CH2', 'CH3', 'CH4'])}
            phaseUnit="deg"
            yScale="linear"
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            minHeight: 56,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
            gap: 2,
          }}
        >
          {!confirmingDiscard ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setConfirmingDiscard(true)}
                disabled={discarding}
              >
                Discard &amp; Exit
              </Button>
              <Button variant="contained" onClick={onProceed}>
                Proceed to Measurement
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  Are you sure? This will cancel the measurement.
                </Typography>
                {discardError && (
                  <Typography variant="caption" color="error" data-testid="discard-error">
                    {discardError}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setConfirmingDiscard(false);
                    setDiscardError(null);
                  }}
                  disabled={discarding}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDiscardConfirm}
                  disabled={discarding}
                  startIcon={discarding ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  Confirm Discard
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
